import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, PlayCircle, Lock } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";

export default function Corsi() {
  const { t } = useLang();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const accessLabel = (c) => c.access === "free" ? t("price_free") : c.access === "premium" ? "Premium" : (c.price > 0 ? `€${c.price}` : t("price_free"));

  return (
    <div data-testid="corsi-page">
      <PageHeader eyebrow={t("nav_courses")} title={t("courses_title")} desc={t("courses_desc")} />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 pb-24">
        {items === null ? (
          <div className="grid sm:grid-cols-2 gap-6">{[...Array(2)].map((_, i) => <div key={i} className="aspect-[16/9] rounded-xl bg-[#FFFFFF] animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title={t("empty_title")} desc={t("empty_desc")} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {items.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}>
                <Link to={`/corsi/${c.id}`} data-testid={`course-card-${c.id}`}
                  className="hover-lift group block rounded-2xl border border-[rgba(61,50,56,0.08)] bg-[#FFFFFF] overflow-hidden hover:border-[rgba(198,137,158,0.4)]">
                  <div className="aspect-[16/9] bg-[#F3E8E2] overflow-hidden relative">
                    {c.cover_path ? <img src={mediaUrl(c.cover_path)} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      : <div className="w-full h-full flex items-center justify-center font-serif text-5xl text-[rgba(198,137,158,0.2)]">{c.title.charAt(0)}</div>}
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-[rgba(13,12,11,0.7)] border border-[rgba(198,137,158,0.3)] rounded-full px-3 py-1 text-xs text-[#C6899E]">
                      {c.access === "premium" && <Lock className="w-3 h-3" />} {accessLabel(c)}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl group-hover:text-[#C6899E] transition-colors">{c.title}</h3>
                    {c.description && <p className="mt-2 text-sm text-[#8C7E83] line-clamp-2">{c.description}</p>}
                    <div className="mt-4 flex items-center gap-5 text-xs text-[#9C8F94] uppercase tracking-[0.12em]">
                      <span className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> {c.module_count} {t("modules")}</span>
                      <span className="flex items-center gap-1.5"><PlayCircle className="w-3.5 h-3.5" /> {c.lesson_count} {t("lessons")}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
